import {
  ColumnDataTypes,
  FilterOperators,
  FilterOptionTypes,
  type ColumnConfig,
  type FilterOptionsParams,
  type DynamicFilterOptionsPage,
} from '@/components/DataTable';
import type { QueryKey } from '@tanstack/react-query';
import { Check, Users as UsersIcon } from 'lucide-react';
import {
  PLUGIN_SOURCE_TO_PLATFORM,
  getPluginSourceLabel,
  type PluginListItem,
  type PluginFilterOptionField,
  type PluginSource,
} from '@kirin/api-client/contracts/plugins';
import { RelativeTimeWithTooltip } from '@/components/shared/RelativeTimeWithTooltip';
import { PluginSourceBadge } from '@/components/shared/plugins/PluginSourceBadge';
import { ClickableUserCount } from '@/components/shared/plugins/ClickableUserCount';
import { PluginUserFilterOption } from '@/components/shared/plugins/PluginUserFilterOption';
import {
  PLUGIN_FIELDS,
  PLUGIN_FILTER_URL_KEYS,
  PLUGIN_SOURCE_OPTIONS,
} from './constants';

/**
 * Display label for a source badge, used to sort the badges within a row so the
 * in-row order matches the backend Source sort (lexicographic by label, KIR-3191).
 * Known sources resolve through the registry; unknown sources fall back to the
 * raw string — mirroring `PluginSourceBadge`'s own label fallback so the order
 * lines up with what is actually rendered.
 */
function sourceLabel(source: string): string {
  return source in PLUGIN_SOURCE_TO_PLATFORM
    ? getPluginSourceLabel(source as PluginSource)
    : source;
}

const EnumOperators = [
  { value: FilterOperators.INCLUDES, label: 'Includes' },
  { value: FilterOperators.EXCLUDES, label: 'Excludes' },
];

const DateOperators = [
  { value: FilterOperators.GREATER_THAN, label: 'Is After' },
  { value: FilterOperators.LESS_THAN, label: 'Is Before' },
  { value: FilterOperators.BETWEEN, label: 'Is Between' },
];

const ValuelessOperators = [
  { value: FilterOperators.IS_EMPTY, label: 'Is Empty' },
  { value: FilterOperators.IS_NOT_EMPTY, label: 'Is Not Empty' },
];

// Both scopes expose value-less operators on every filterable column.
const EnumOperatorsWithValueless = [...EnumOperators, ...ValuelessOperators];
const DateOperatorsWithValueless = [...DateOperators, ...ValuelessOperators];

/**
 * Scope-agnostic filter wiring for the dynamic Plugins filters. Each tab
 * supplies how to build the query key + fetch options for a field, plus whether
 * the canonical path honors a Source-column sort. Org binds it to the org
 * filter-options endpoint; personal to `/account/plugins/filter-options`.
 */
export interface PluginFilterScope {
  getQueryKey: (
    field: PluginFilterOptionField,
    params: FilterOptionsParams
  ) => QueryKey;
  queryFn: (
    field: PluginFilterOptionField,
    params: FilterOptionsParams
  ) => Promise<DynamicFilterOptionsPage>;
  enableSourceSort: boolean;
}

interface CreatePluginColumnsOptions {
  /** Render the per-plugin Users column (org admin view only). */
  showUsersColumn: boolean;
  /**
   * Click handler for the Users count. When provided, the count is rendered
   * via `ClickableUserCount` and drills into the plugin user list modal
   * (matches the legacy KIR-2844 behavior).
   */
  onUserCountClick?: (plugin: PluginListItem) => void;
  /**
   * Scope descriptor that wires the dynamic Name/Marketplace/Users filters, the
   * First Seen date filter, and the Source sort. When omitted, none of those
   * render. Both tabs supply it; Source stays statically filterable regardless.
   */
  pluginFilters?: PluginFilterScope;
}

/**
 * Column definitions for the Plugins DataTable, shared by the org and personal
 * tabs. The Users column is gated on `showUsersColumn` (org admin only); the
 * dynamic filters + Source sort are wired from the scope-agnostic
 * `pluginFilters` descriptor.
 */
export function createPluginColumns({
  showUsersColumn,
  onUserCountClick,
  pluginFilters,
}: CreatePluginColumnsOptions): ColumnConfig<PluginListItem>[] {
  // Build the dynamic-ENUM filter config for one field via the scope descriptor.
  const dynamicEnumFilter = (
    scope: PluginFilterScope,
    field: PluginFilterOptionField,
    urlKey: string,
    limit: number,
    render?: (
      option: { value: string; metadata?: Record<string, unknown> },
      isSelected: boolean
    ) => React.ReactNode
  ) =>
    ({
      filterable: true as const,
      dataType: ColumnDataTypes.ENUM,
      filterOperators: EnumOperatorsWithValueless,
      statePersistence: { urlKey },
      filterOptions: {
        type: FilterOptionTypes.DYNAMIC,
        getQueryKey: (params: FilterOptionsParams) =>
          scope.getQueryKey(field, params),
        queryFn: (params: FilterOptionsParams) => scope.queryFn(field, params),
        pagination: { mode: 'offset' as const, limit },
        queryConfig: { staleTime: 2 * 60 * 1000 },
        ...(render ? { render } : {}),
      },
    }) satisfies Partial<ColumnConfig<PluginListItem>>;

  const nameFilter = pluginFilters
    ? dynamicEnumFilter(
        pluginFilters,
        'name',
        PLUGIN_FILTER_URL_KEYS[PLUGIN_FIELDS.NAME],
        20
      )
    : {};

  const marketplaceFilter = pluginFilters
    ? dynamicEnumFilter(
        pluginFilters,
        'marketplace',
        PLUGIN_FILTER_URL_KEYS[PLUGIN_FIELDS.MARKETPLACE],
        20
      )
    : {};

  // First Seen / Last Seen share the identical date-filter config — only the URL
  // key differs. A present (even empty) filterOptions is required for the header
  // to render the date-filter trigger (matches org Extensions/MCP tabs).
  const dateFilter = (urlKey: string) =>
    pluginFilters
      ? ({
          filterable: true as const,
          dataType: ColumnDataTypes.DATE,
          filterOperators: DateOperatorsWithValueless,
          statePersistence: { urlKey },
          filterOptions: {
            type: FilterOptionTypes.STATIC,
            options: [],
          },
        } satisfies Partial<ColumnConfig<PluginListItem>>)
      : {};

  const firstSeenFilter = dateFilter(
    PLUGIN_FILTER_URL_KEYS[PLUGIN_FIELDS.FIRST_SEEN_AT]
  );
  const lastSeenFilter = dateFilter(
    PLUGIN_FILTER_URL_KEYS[PLUGIN_FIELDS.LAST_SEEN_AT]
  );

  // Source sort (KIR-3191 D6) is wired only when the scope's canonical path
  // honors it (`enableSourceSort`). Both scopes now do.
  const sourceSort = pluginFilters?.enableSourceSort
    ? { sortable: true as const, sortField: 'source', sortKey: 'src' }
    : {};

  const columns: ColumnConfig<PluginListItem>[] = [
    {
      id: PLUGIN_FIELDS.NAME,
      header: 'Name',
      // Use `||` here too, not `??`. An empty-string displayName is treated as
      // "no display name" by the renderer below; the accessor must agree or
      // the sort key and the visible label desync.
      accessor: row => row.displayName || row.pluginName,
      width: 280,
      renderCell: ({ row }) => (
        <span className="font-medium">{row.displayName || row.pluginName}</span>
      ),
      sortable: true,
      sortField: 'name',
      sortKey: 'n',
      ...nameFilter,
    },
    {
      id: PLUGIN_FIELDS.SOURCE,
      header: 'Source',
      accessor: 'sources',
      width: 200,
      renderCell: ({ row }) => {
        // Order badges by display label so the in-row order matches the backend
        // Source sort (lexicographic by label, KIR-3191). Sorted here in the
        // render body — not inline in the JSX map.
        const sortedSources = [...row.sources].sort((a, b) =>
          sourceLabel(a).localeCompare(sourceLabel(b))
        );
        return (
          <div className="flex flex-wrap gap-1">
            {sortedSources.map(src => (
              <PluginSourceBadge key={src} source={src} />
            ))}
          </div>
        );
      },
      ...sourceSort,
      filterable: true,
      dataType: ColumnDataTypes.ENUM,
      filterOperators: EnumOperatorsWithValueless,
      statePersistence: {
        urlKey: PLUGIN_FILTER_URL_KEYS[PLUGIN_FIELDS.SOURCE],
      },
      filterOptions: {
        type: FilterOptionTypes.STATIC,
        options: PLUGIN_SOURCE_OPTIONS,
        // Match the Extensions Source filter pattern: a check icon for the
        // selected option(s) so users get a clear visual cue that the click
        // landed — `PluginSourceBadge` has its own fixed styling, so leaning
        // on isSelected alone (font weight, container bg) is invisible.
        render: (option, isSelected) => (
          <div className="flex items-center gap-2">
            {isSelected && (
              <Check className="h-3.5 w-3.5 text-primary shrink-0" />
            )}
            <PluginSourceBadge source={option.value} />
          </div>
        ),
      },
    },
    {
      id: PLUGIN_FIELDS.MARKETPLACE,
      header: 'Marketplace',
      accessor: 'marketplace',
      width: 160,
      // `||` not `??`: an empty-string marketplace must fall back to the dash
      // too, otherwise the cell renders blank (matches the Name accessor).
      renderCell: ({ row }) =>
        row.marketplace || <span className="text-muted-foreground">—</span>,
      sortable: true,
      sortField: 'marketplace',
      sortKey: 'mk',
      ...marketplaceFilter,
    },
  ];

  if (showUsersColumn) {
    const usersFilter = pluginFilters
      ? dynamicEnumFilter(
          pluginFilters,
          'users',
          PLUGIN_FILTER_URL_KEYS[PLUGIN_FIELDS.USER],
          10,
          (option, isSelected) => (
            <PluginUserFilterOption
              userId={option.value}
              userName={(option.metadata?.name as string | null) ?? null}
              userEmail={(option.metadata?.email as string | null) ?? null}
              isSelected={isSelected}
            />
          )
        )
      : {};

    columns.push({
      id: PLUGIN_FIELDS.USER,
      header: 'Users',
      accessor: 'userCount',
      width: 120,
      renderCell: ({ row }) =>
        onUserCountClick ? (
          <ClickableUserCount
            count={row.userCount}
            tooltipScope="with this plugin"
            onClick={e => {
              // Don't toggle row expansion when clicking the user count.
              e.stopPropagation();
              onUserCountClick(row);
            }}
          />
        ) : (
          <span className="inline-flex items-center gap-1.5 font-medium">
            <UsersIcon className="h-3.5 w-3.5" />
            {row.userCount}
          </span>
        ),
      sortable: true,
      // The column id is `users` (the filter field); sort still keys off the
      // aggregated count.
      sortField: 'userCount',
      sortKey: 'uc',
      ...usersFilter,
    });
  }

  columns.push({
    id: PLUGIN_FIELDS.FIRST_SEEN_AT,
    header: 'First Seen',
    timestamp: true,
    accessor: 'firstSeenAt',
    renderCell: ({ row }) => (
      <span className="text-muted-foreground">
        <RelativeTimeWithTooltip date={row.firstSeenAt} />
      </span>
    ),
    sortable: true,
    sortField: 'firstSeenAt',
    sortKey: 'fs',
    ...firstSeenFilter,
  });

  columns.push({
    id: PLUGIN_FIELDS.LAST_SEEN_AT,
    header: 'Last Seen',
    timestamp: true,
    accessor: 'lastSeenAt',
    renderCell: ({ row }) => <RelativeTimeWithTooltip date={row.lastSeenAt} />,
    sortable: true,
    sortField: 'lastSeenAt',
    sortKey: 'ls',
    ...lastSeenFilter,
  });

  return columns;
}
